import type { FilterState } from '../filters/Filters';

/** Map a colour-by / split lens id → the FilterState key whose value-set filters that field.
 *  Only categorical lenses that have a corresponding value filter are listed; sequential/derived/
 *  boolean lenses (buildOrder, value, pctCO, exceptions, lakeview…) are intentionally absent, so a
 *  breakdown row under them does not become a click-to-filter. Most ids map 1:1; the three renames
 *  (permitStatus→permitIssued, package→pkg, buildStatus→buildStatusRaw) mirror Filters.tsx. */
export const LENS_FILTER_KEY: Partial<Record<string, keyof FilterState>> = {
  landUse: 'landUse', sales: 'sales', phase: 'phase', subPhase: 'subPhase', concurrency: 'concurrency',
  // permitStatus is intentionally NOT here: its lens now has a cross-field 'Closed' bucket (issued +
  // openClosed=Closed/Completed) that can't be expressed as a single permitIssued value, so a
  // breakdown click ISOLATES instead of filtering. (Rail's "Permit issued" filter still covers it.)
  openClosed: 'openClosed', buildStatus: 'buildStatusRaw',
  model: 'model', package: 'pkg', orientation: 'orientation', version: 'version', modelVersion: 'modelVersion',
  ghlContract: 'contractStatus', county: 'county',   // Contract Status lens ↔ the live contractStatus rail filter (same field)
  constructionUpgrades: 'constructionUpgrades', financialIncentives: 'financialIncentives',
  jobContingency: 'jobContingency', additionalContingencies: 'additionalContingencies',
};

/** Whether a lens has a categorical value-filter (clicking a breakdown row under it toggles a real
 *  rail filter). Boolean/sequential/derived lenses (sidewalk, lakeview, easement, buildOrder, value
 *  …) return false — callers fall back to isolating the category so the click still scopes the grid. */
export const isLensFilterable = (lensId: string): boolean => !!LENS_FILTER_KEY[lensId];

/** The FilterState keys owned by the breakdown lenses (primary colour-by + active splits). These are
 *  the filters the analytics panel "faceted" view LIFTS so every category of a clicked field stays
 *  listed and multi-selectable — the field's own selection shouldn't hide its other options. Lenses
 *  with no value-filter (buildOrder, value, booleans) contribute nothing. */
export function breakdownFacetKeys(lensIds: string[]): Set<keyof FilterState> {
  const keys = new Set<keyof FilterState>();
  for (const id of lensIds) { const k = LENS_FILTER_KEY[id]; if (k) keys.add(k); }
  return keys;
}

/** A copy of `filters` with the breakdown lenses' own value-sets emptied — the faceted universe the
 *  panel breaks down. Every OTHER filter still applies, so the facet is the selection widened only
 *  along its own dimensions. Returns the SAME object when nothing is lifted (no breakdown filter
 *  active / no filterable breakdown lens), so callers can cheaply skip recomputing the set. */
export function facetFilters(filters: FilterState, lensIds: string[]): FilterState {
  const keys = breakdownFacetKeys(lensIds);
  let changed = false;
  const next: FilterState = { ...filters };
  for (const k of keys) {
    if ((next[k] as Set<string>).size) { (next[k] as Set<string>) = new Set(); changed = true; }
  }
  return changed ? next : filters;
}

/** A breakdown row's DISPLAY bucket key → the raw field value the filter matches against. Most
 *  lenses' cat() == the raw value, but a few SYNTHESIZE the label, so this must be lens-aware:
 *   - '—' / '(none)' are always the blank field value.
 *   - 'No permit' is a LITERAL value for Permit status (permitIssued stores it), so it filters by
 *     itself; for every other lens (e.g. Open/Closed) it's synthesized over an empty field → ''.
 *   - Phase shows 'Phase 1' but the field stores the bare '1' — strip the prefix.
 *  Getting this wrong makes the row filter match ZERO lots (the bug this fixes for No permit + Phase). */
export function bucketToFilterValue(bucketKey: string, lensId?: string): string {
  if (bucketKey === '—' || bucketKey === '(none)') return '';
  if (/^no (permit|build order)$/i.test(bucketKey)) return lensId === 'permitStatus' ? bucketKey : '';
  if (lensId === 'phase') return bucketKey.replace(/^Phase\s+/, '');
  return bucketKey;
}

/** Resolve a breakdown path (lens ids + bucket keys, primary first) to the (filterKey, value) pairs
 *  it controls, dropping any level whose lens has no value filter. */
function pairsFor(lensIds: string[], bucketKeys: string[]): { key: keyof FilterState; val: string }[] {
  const out: { key: keyof FilterState; val: string }[] = [];
  for (let i = 0; i < lensIds.length; i++) {
    const key = LENS_FILTER_KEY[lensIds[i]];
    if (key) out.push({ key, val: bucketToFilterValue(bucketKeys[i], lensIds[i]) });
  }
  return out;
}

/** Is the breakdown row at this path currently filtered ON? True only when EVERY supported level's
 *  value is present in its filter set (an intersection is "on" only if all its parts are). A path
 *  with no supported levels is never considered filtered. */
export function isBreakdownFiltered(filters: FilterState, lensIds: string[], bucketKeys: string[]): boolean {
  const pairs = pairsFor(lensIds, bucketKeys);
  return pairs.length > 0 && pairs.every(p => (filters[p.key] as Set<string>).has(p.val));
}

/** Toggle the filter for a breakdown row. If the row is currently filtered ON, remove each of its
 *  values; otherwise add them (an intersection across fields, since FilterState ANDs across keys
 *  and ORs within a key). Returns a NEW FilterState (new Sets for touched keys); unsupported levels
 *  are skipped. Binary on/off — no tri-state negation (that lives on the rail filter chips). */
export function toggleBreakdownFilter(filters: FilterState, lensIds: string[], bucketKeys: string[]): FilterState {
  const pairs = pairsFor(lensIds, bucketKeys);
  if (!pairs.length) return filters;
  const on = isBreakdownFiltered(filters, lensIds, bucketKeys);
  const next: FilterState = { ...filters };
  for (const p of pairs) {
    const s = new Set(next[p.key] as Set<string>);
    if (on) s.delete(p.val); else s.add(p.val);
    (next[p.key] as Set<string>) = s;
  }
  return next;
}
